import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Ico } from '@/ui/Ico'
import { cls } from '@/ui/cls'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import {
  DATE_OPS, NUM_OPS, TEXT_OPS, isTicked, setMode, tickValues, toggleValue, untickValues,
  type DateFilter, type NumFilter, type SetFilter, type TextFilter,
} from './filterModel'

export type SortDir = 'asc' | 'desc' | null

export interface FilterValue {
  value: string
  label: string
  count: number
}

interface BaseProps {
  label: string
  sort: SortDir
  /** Position in the sort chain, 1-based; 0 when this column is not sorted.
   *  Shown so a tie-breaker is distinguishable from the primary sort. */
  sortRank?: number
  onSort: (dir: SortDir) => void
  numeric?: boolean
}

interface MultiProps extends BaseProps {
  mode: 'multi'
  values: FilterValue[]
  selected: SetFilter
  onSelectedChange: (s: SetFilter) => void
  /** Optional numeric range on the same column, applied alongside the values. */
  range?: NumFilter
  onRangeChange?: (r: NumFilter) => void
  /** Optional date range on the same column. */
  dateRange?: DateFilter
  onDateRangeChange?: (r: DateFilter) => void
}

interface TextProps extends BaseProps {
  mode: 'text'
  text: TextFilter
  onTextChange: (t: TextFilter) => void
  placeholder?: string
}

type Props = MultiProps | TextProps

const POPOVER_W = 260
const VIEWPORT_PAD = 8

// Excel-style autofilter dropdown — single menu per column that does both
// sort (top of menu) and filter (rest). Rendered via a portal so it isn't
// clipped by the table's horizontal scroll container; positioned next to
// the trigger button and clamped to the viewport.
export default function ColumnFilter(props: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Position the popover based on the trigger's bounding rect. Right-align
  // by default; flip to left-align if it would overflow the viewport.
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const btn = btnRef.current
      if (!btn) return
      const r = btn.getBoundingClientRect()
      let left = r.right - POPOVER_W
      if (left < VIEWPORT_PAD) left = r.left  // flip
      left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - POPOVER_W - VIEWPORT_PAD))
      const top = r.bottom + 4
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  // Close on outside click, Esc/Enter, and on any scroll anywhere — same
  // behavior as Excel autofilter (scrolling the sheet closes open menus).
  useEffect(() => {
    if (!open) return
    const onMouse = (e: MouseEvent) => {
      const t = e.target as Node
      if (popRef.current?.contains(t)) return
      if (btnRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') setOpen(false)
    }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onMouse)
    document.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onScroll, true)  // capture: catch nested scrolls
    return () => {
      document.removeEventListener('mousedown', onMouse)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const hasFilter =
    props.mode === 'multi'
      ? props.selected.values.size > 0
        || (props.range ? props.range.op !== 'any' : false)
        || (props.dateRange ? props.dateRange.op !== 'any' : false)
      : props.text.op === 'empty' || props.text.op === 'notEmpty'
        || props.text.value.trim().length > 0

  const ascLabel = props.numeric ? 'Minst först' : 'Sortera A → Ö'
  const descLabel = props.numeric ? 'Störst först' : 'Sortera Ö → A'

  const clear = () => {
    if (props.mode === 'multi') {
      props.onSelectedChange({ mode: 'include', values: new Set() })
      props.onRangeChange?.({ op: 'any', a: null, b: null })
      props.onDateRangeChange?.({ op: 'any', from: null, to: null })
    } else {
      props.onTextChange({ op: 'contains', value: '' })
    }
  }

  // The popover is portalled and fixed-position, so animating it costs one
  // element regardless of how many rows the table is showing.
  const popover = pos && (
    <AnimatePresence>
      {open && (
      <m.div
        ref={popRef}
        className="col-filter-popover col-filter-popover-fixed"
        style={{ top: pos.top, left: pos.left }}
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, y: -6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4, scale: 0.99 }}
        transition={{ duration: 0.14, ease: [0.2, 0.8, 0.2, 1] }}
      >
      <div className="col-filter-head">
        <span className="text-[11px] uppercase tracking-[0.08em] text-ink-700">{props.label}</span>
        <button
          className="cf-close"
          onClick={() => setOpen(false)}
          aria-label="Stäng"
          type="button"
        >
          {Ico.x}
        </button>
      </div>

      <div className="col-filter-sort">
        <button
          type="button"
          className={cls(
            'text-left px-2.5 py-1.5 text-xs border-0 text-ink-1000 cursor-pointer rounded-sm',
            props.sort === 'asc'
              ? 'bg-ink-100 font-semibold'
              : 'bg-transparent hover:bg-ink-100',
          )}
          onClick={() => props.onSort(props.sort === 'asc' ? null : 'asc')}
        >
          ↑ {ascLabel}{props.sort === 'asc' && rankBadge(props.sortRank)}
        </button>
        <button
          type="button"
          className={cls(
            'text-left px-2.5 py-1.5 text-xs border-0 text-ink-1000 cursor-pointer rounded-sm',
            props.sort === 'desc'
              ? 'bg-ink-100 font-semibold'
              : 'bg-transparent hover:bg-ink-100',
          )}
          onClick={() => props.onSort(props.sort === 'desc' ? null : 'desc')}
        >
          ↓ {descLabel}{props.sort === 'desc' && rankBadge(props.sortRank)}
        </button>
      </div>

      {props.mode === 'multi' ? (
        <>
          {props.range && props.onRangeChange && (
            <NumRange value={props.range} onChange={props.onRangeChange} />
          )}
          {props.dateRange && props.onDateRangeChange && (
            <DateRange value={props.dateRange} onChange={props.onDateRangeChange} />
          )}
          <MultiContent
            values={props.values}
            selected={props.selected}
            onSelectedChange={props.onSelectedChange}
            search={search}
            setSearch={setSearch}
          />
        </>
      ) : (
        <TextContent
          filter={props.text}
          onChange={props.onTextChange}
          placeholder={props.placeholder || `Sök ${props.label.toLowerCase()}…`}
        />
      )}

      <div className="col-filter-foot">
        <button
          type="button"
          className="cf-clear"
          onClick={clear}
          disabled={!hasFilter}
        >
          Rensa filter
        </button>
        <button
          type="button"
          className="px-3.5 py-[5px] text-xs font-semibold bg-ink-1000 text-white border-0 rounded-sm cursor-pointer hover:bg-ink-900"
          onClick={() => setOpen(false)}
        >
          Klar
        </button>
      </div>
      </m.div>
      )}
    </AnimatePresence>
  )

  return (
    <div className="col-filter">
      <button
        ref={btnRef}
        type="button"
        className={cls('col-filter-btn', hasFilter && 'col-filter-btn-filtered')}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(o => !o)
        }}
        title={`Sortera/filtrera ${props.label}`}
      >
        {Ico.filter}
      </button>
      {popover && createPortal(popover, document.querySelector('.app') ?? document.body)}
    </div>
  )
}

function MultiContent({
  values, selected, onSelectedChange, search, setSearch,
}: {
  values: FilterValue[]
  selected: SetFilter
  onSelectedChange: (s: SetFilter) => void
  search: string
  setSearch: (s: string) => void
}) {
  const visible = values.filter(v =>
    v.label.toLowerCase().includes(search.toLowerCase())
  )

  const { mode } = selected
  const visibleValues = visible.map(v => v.value)

  return (
    <div className="px-2 pt-2 pb-1">
      <div className="flex items-center gap-1 px-1 pb-2">
        {(['include', 'exclude'] as const).map(m => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => onSelectedChange(setMode(selected, m))}
            className={cls(
              'px-2 py-0.5 text-[11px] rounded-sm border cursor-pointer',
              mode === m
                ? 'bg-ink-1000 border-ink-1000 text-white font-semibold'
                : 'bg-transparent border-border text-ink-500 hover:text-ink-1000',
            )}
          >
            {m === 'include' ? 'Endast dessa' : 'Alla utom'}
          </button>
        ))}
      </div>
      <input
        type="text"
        className="cf-search"
        placeholder="Sök…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <div className="cf-bulk">
        <button
          type="button"
          className="cf-bulk-btn"
          onClick={() => onSelectedChange(tickValues(selected, visibleValues))}
        >
          Markera synliga
        </button>
        <span className="text-ink-300">·</span>
        <button
          type="button"
          className="cf-bulk-btn"
          onClick={() => onSelectedChange(untickValues(selected, visibleValues))}
        >
          Avmarkera synliga
        </button>
      </div>
      <ul className="cf-list">
        {visible.length === 0 && <li className="p-3 text-center text-ink-500 italic bg-white">Inga träffar</li>}
        {visible.map(v => (
          <li key={v.value} className="cf-row">
            <label className="cf-row-label">
              <input
                type="checkbox"
                checked={isTicked(selected, v.value)}
                onChange={() => onSelectedChange(toggleValue(selected, v.value))}
              />
              <span className="cf-row-text">{v.label}</span>
              <span className="shrink-0 font-mono text-[10px] text-ink-500 bg-ink-100 px-1.5 py-px rounded-lg">{v.count}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}

const OP_SELECT =
  'w-full px-2 py-1 text-xs text-ink-1000 bg-white border border-border rounded-sm cursor-pointer'
const BOUND_INPUT =
  'w-full px-2 py-1 text-xs text-ink-1000 bg-white border border-border rounded-sm'

/** The chain position, so "sorted 2nd" is visible rather than implied. */
function rankBadge(rank?: number) {
  if (!rank || rank < 2) return null
  return <span className="ml-1 text-[10px] text-ink-500 tabular-nums">{rank}</span>
}

function TextContent({
  filter, onChange, placeholder,
}: {
  filter: TextFilter
  onChange: (t: TextFilter) => void
  placeholder: string
}) {
  const spec = TEXT_OPS.find(o => o.op === filter.op) ?? TEXT_OPS[0]
  return (
    <div className="p-3 flex flex-col gap-2">
      <select
        className={OP_SELECT}
        value={filter.op}
        onChange={e => {
          const op = e.target.value as TextFilter['op']
          const needs = TEXT_OPS.find(o => o.op === op)?.needsValue
          // Drop the term when switching to blank / not-blank, so it does not
          // sit invisibly in state and reappear on the next operator change.
          onChange({ op, value: needs ? filter.value : '' })
        }}
        aria-label="Matchningstyp"
      >
        {TEXT_OPS.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
      </select>
      {spec.needsValue && (
        <input
          type="text"
          className="cf-search"
          placeholder={placeholder}
          value={filter.value}
          onChange={e => onChange({ ...filter, value: e.target.value })}
          autoFocus
        />
      )}
    </div>
  )
}

function NumRange({ value, onChange }: { value: NumFilter; onChange: (v: NumFilter) => void }) {
  const bounds = NUM_OPS.find(o => o.op === value.op)?.bounds ?? 0
  const num = (s: string) => (s.trim() === '' ? null : Number(s))
  return (
    <div className="px-3 pt-3 flex flex-col gap-2 border-b border-border pb-3">
      <select
        className={OP_SELECT}
        value={value.op}
        onChange={e => onChange({ ...value, op: e.target.value as NumFilter['op'] })}
        aria-label="Intervalltyp"
      >
        {NUM_OPS.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
      </select>
      {bounds > 0 && (
        <div className="flex items-center gap-1.5">
          <input
            type="number" className={BOUND_INPUT} aria-label="Från"
            value={value.a ?? ''}
            onChange={e => onChange({ ...value, a: num(e.target.value) })}
          />
          {bounds === 2 && (
            <>
              <span className="text-[11px] text-ink-500">och</span>
              <input
                type="number" className={BOUND_INPUT} aria-label="Till"
                value={value.b ?? ''}
                onChange={e => onChange({ ...value, b: num(e.target.value) })}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function DateRange({ value, onChange }: { value: DateFilter; onChange: (v: DateFilter) => void }) {
  const bounds = DATE_OPS.find(o => o.op === value.op)?.bounds ?? 0
  const day = (s: string) => (s.trim() === '' ? null : s)
  return (
    <div className="px-3 pt-3 flex flex-col gap-2 border-b border-border pb-3">
      <select
        className={OP_SELECT}
        value={value.op}
        onChange={e => onChange({ ...value, op: e.target.value as DateFilter['op'] })}
        aria-label="Datumintervalltyp"
      >
        {DATE_OPS.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
      </select>
      {bounds > 0 && (
        <div className="flex items-center gap-1.5">
          <input
            type="date" className={BOUND_INPUT} aria-label="Från"
            value={value.from ?? ''}
            onChange={e => onChange({ ...value, from: day(e.target.value) })}
          />
          {bounds === 2 && (
            <>
              <span className="text-[11px] text-ink-500">till</span>
              <input
                type="date" className={BOUND_INPUT} aria-label="Till"
                value={value.to ?? ''}
                onChange={e => onChange({ ...value, to: day(e.target.value) })}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}
